import React, { useState, useMemo, useCallback } from "react"
import { injectStripe, ReactStripeElements } from "react-stripe-elements"
import {
    Button,
    ExclusiveChoiceDropdown,
    GridContainer,
    GridRow,
    GridCol,
    SingleLineTextInput
} from "dash-styled"
import { APIBillingAddressModel } from "../../../../../../../Models/API/BillingModel"
import { StripePaymentMethodInput } from "../../../../Common/StripePaymentMethodInput/StripePaymentMethodInput"
import countryCodes from "../../../../../../../Helpers/Data/countryCodes.json"
import { useInput } from "../../../../../../../Hooks/useInput"
import { useAsyncCommand } from "../../../../../../../Hooks/useAsyncCommand"

export interface PaymentMethodFormProps {
    onError: (message: string) => void
    onSubmit: (
        last4Digits: string,
        token: string,
        address: APIBillingAddressModel
    ) => Promise<void>
    className?: string
}

type Props = PaymentMethodFormProps & ReactStripeElements.InjectedStripeProps

const allCountryChoices = Object.values(countryCodes)
const countryChoicesKeys = Object.keys(countryCodes)

export const PaymentMethodForm = injectStripe(
    ({ stripe, onSubmit, onError, className }: Props) => {
        const [stripeReady, setStripeReady] = useState(false)
        const [countrySearch, setCountrySearch] = useState("")
        const countryChoices = useMemo(
            () =>
                allCountryChoices.filter(country =>
                    country
                        .toLocaleLowerCase()
                        .includes(countrySearch.toLocaleLowerCase())
                ),
            [countrySearch]
        )
        const [name, setName] = useState("")
        const address1 = useInput("", false)
        const address2 = useInput("", true)
        const city = useInput("", false)
        const state = useInput("", false)
        const zip = useInput("", false)
        const [country, setCountry] = useState<string | undefined>(undefined)
        const countryIndex =
            country === undefined ? -1 : countryChoices.indexOf(country)
        const canSubmit =
            stripeReady &&
            address1.isValid &&
            address2.isValid &&
            city.isValid &&
            state.isValid &&
            zip.isValid &&
            country !== undefined
        const { isProcessing, perform } = useAsyncCommand(
            stripe === undefined || !canSubmit || country === undefined
                ? undefined
                : async () => {
                      const { error, token } = await stripe.createToken({
                          name,
                          address_line1: address1.value,
                          address_line2: address2.value,
                          address_city: city.value,
                          address_state: state.value,
                          address_zip: zip.value,
                          address_country: countryChoicesKeys[countryIndex]
                      })

                      if (
                          error !== undefined ||
                          token === undefined ||
                          token.card === undefined
                      ) {
                          onError(
                              error === undefined || error.message === undefined
                                  ? "Unknown error in registering credit card information."
                                  : error.message
                          )
                          return
                      }

                      await onSubmit(token.card.last4, token.id, {
                          name,
                          line1: address1.value,
                          line2: address2.value,
                          city: city.value,
                          state: state.value,
                          postalCode: zip.value,
                          country
                      })
                  }
        )
        const handleChangeStripe = (isComplete: boolean, newName: string) => {
            setStripeReady(isComplete)
            setName(newName)
        }
        const handleChangeCountry = useCallback(
            (i: number) => setCountry(countryChoices[i]),
            [countryChoices]
        )

        return (
            <GridContainer className={className}>
                <GridRow xs="justifyContentCenter">
                    <StripePaymentMethodInput onChange={handleChangeStripe} />
                </GridRow>
                <form style={{ maxWidth: "400px", margin: "0 auto" }}>
                    <GridRow xs="justifyContentCenter">
                        <GridCol>
                            <SingleLineTextInput
                                autoComplete="off"
                                name="ccAddress1"
                                label="Address line 1"
                                onChange={address1.onChange}
                                value={address1.value}
                                className="mb-1"
                            />
                        </GridCol>
                    </GridRow>
                    <GridRow xs="justifyContentCenter">
                        <GridCol>
                            <SingleLineTextInput
                                autoComplete="off"
                                name="ccAddress2"
                                label="Address line 2"
                                onChange={address2.onChange}
                                value={address2.value}
                                className="mb-1"
                            />
                        </GridCol>
                    </GridRow>
                    <GridRow xs="justifyContentCenter" className="mb-1">
                        <GridCol>
                            <SingleLineTextInput
                                autoComplete="off"
                                name="ccCity"
                                label="City"
                                onChange={city.onChange}
                                value={city.value}
                            />
                        </GridCol>
                        <GridCol>
                            <SingleLineTextInput
                                autoComplete="off"
                                name="ccState"
                                label="State"
                                onChange={state.onChange}
                                value={state.value}
                            />
                        </GridCol>
                        <GridCol>
                            <SingleLineTextInput
                                autoComplete="off"
                                name="ccZip"
                                label="Zip"
                                onChange={zip.onChange}
                                value={zip.value}
                            />
                        </GridCol>
                    </GridRow>
                    <GridRow xs="justifyContentCenter">
                        <GridCol>
                            <ExclusiveChoiceDropdown
                                label="Country"
                                onSearch={setCountrySearch}
                                choices={countryChoices}
                                selectedIndex={countryIndex}
                                onClick={handleChangeCountry}
                            />
                        </GridCol>
                    </GridRow>
                    <GridRow xs="justifyContentCenter" marginTop="6">
                        <Button
                            variant="contained"
                            color="darkviolet"
                            onClick={perform}
                            disabled={!canSubmit || isProcessing}
                        >
                            {isProcessing ? "Adding..." : "Add card"}
                        </Button>
                    </GridRow>
                </form>
            </GridContainer>
        )
    }
)
